import React, { useState, useEffect, useCallback } from 'react';
import './RedirectLinks.css';
import { useAuth } from '@clerk/clerk-react';
import axios from 'axios';
import { Trash2 } from 'lucide-react';
import lovableLogo from '../FunnelBuilder/lovable-color.svg';

// Raw source of the tracking scripts, used to inline the code into the Lovable prompt.
/* eslint-disable import/no-webpack-loader-syntax */
import trackerScriptRaw from '!!raw-loader!../FunnelBuilder/trackerScript.js';
import thankYouScriptRaw from '!!raw-loader!../FunnelBuilder/thankYouScript.js';
import urgencyLinkScriptRaw from '!!raw-loader!./urgencyLinkScript.js';
import urgencyLinkSaleScriptRaw from '!!raw-loader!./urgencyLinkSaleScript.js';
/* eslint-enable import/no-webpack-loader-syntax */

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

/* ── Tracking script helpers (mirrors FunnelMapper) ── */
const buildTrackerScript = (offerName, username) =>
    `<meta name="username" content="${username}">\n<meta name="offer" content="${offerName.trim()}">\n<script src="https://d15dfsr886zcp9.cloudfront.net/tracker_script.js" defer></script>`;

const buildThankYouScript = (offerName, username, price) => {
    const usernameTag = `<meta name="username" content="${username}">`;
    const metaTag    = `<meta name="offer" content="${offerName.trim()}">`;
    let priceTag = '';
    if (price)    priceTag = `<meta name="RevTrackPrice" content="${price}">\n`;
    return `${usernameTag}\n${metaTag}\n${priceTag}<script src="https://d15dfsr886zcp9.cloudfront.net/thankYou_script.js" defer></script>`;
};

/* ── Lovable prompt helpers ──
   For Lovable sites we can't load external <script src>, so we build a prompt that tells
   Lovable to inline the meta tags + the script source (run on page load) in a useEffect. */
const LOVABLE_PROMPT_INTRO = 'Add the following meta tags & scripts into the page inside a useEffect. Do not make any modifications to the code. In the useEffect, use Javascript to add the meta tags, do not add them directly into index.html. When the page loads, set the 2 scripts to run.';

const stripScriptTags = (code) =>
    code.replace(/\n?<script\b[^>]*><\/script>/gi, '').replace(/\n{3,}/g, '\n\n').trim();

const makeRunnable = (raw, init) =>
    `${raw.replace(/export\s+function/g, 'function').trim()}\n\n${init}();`;

// metaCodes: array of normal snippets (meta tags + script tag) — the script tag is stripped.
// scripts: array of { raw, init } whose source is inlined and invoked.
const buildLovablePrompt = (metaCodes, scripts) => {
    const metaTags = metaCodes.map(stripScriptTags).filter(Boolean).join('\n');
    const js = scripts.map(({ raw, init }) => makeRunnable(raw, init)).join('\n\n');
    return `${LOVABLE_PROMPT_INTRO}\n\n${metaTags}\n\n${js}`;
};

const RedirectLinks = () => {
    const { getToken, userId: username } = useAuth();
    const [offerUrl, setOfferUrl] = useState('');
    const [defaultUrl, setDefaultUrl] = useState('');
    const [linkName, setLinkName] = useState('');
    const [conditionType, setConditionType] = useState('clicks');
    const [conditionValue, setConditionValue] = useState('');
    const [showTimer, setShowTimer] = useState(true);
    // On-screen badge style. Defaults match the current brand gradient per
    // condition type so legacy links keep their look.
    const [timerPosition, setTimerPosition] = useState('bottom-right');
    const [timerGradientStart, setTimerGradientStart] = useState('#667eea');
    const [timerGradientEnd, setTimerGradientEnd] = useState('#764ba2');
    const [timerTextColor, setTimerTextColor] = useState('#ffffff');
    // Customizable small uppercase text shown above the timer/counter on the
    // badge. The per-condition defaults (Offer Expires In / Spots Remaining
    // / Offer Closes Soon) are the same values the live script falls back
    // to when this field is null in the database.
    const defaultTimerLabel = (type) => {
        if (type === 'timeframe') return 'Offer Expires In';
        if (type === 'sales') return 'Spots Remaining';
        return 'Offer Closes Soon';
    };
    const [timerLabel, setTimerLabel] = useState(() => defaultTimerLabel('clicks'));

    // Customizable main text shown under the badge label for clicks/sales
    // counters (e.g. "Only {count} clicks left"). `{count}` is replaced with
    // the remaining value at render time. Mirrors the on-page script: empty
    // value falls back to the per-condition default.
    const defaultTimerSpotsText = (type) => {
        if (type === 'sales') return 'Only {count} left';
        return 'Only {count} clicks left';
    };
    const [timerSpotsText, setTimerSpotsText] = useState(() => defaultTimerSpotsText('clicks'));

    // When the user switches condition type, swap in the matching default
    // gradient only if the current colours still match the previous default
    // (i.e. the user hasn't customised them yet). Do the same for the
    // badge label so the preview matches the new condition.
    useEffect(() => {
        if (conditionType === 'timeframe') {
            if (timerGradientStart === '#f5576c' && timerGradientEnd === '#f093fb') {
                setTimerGradientStart('#667eea');
                setTimerGradientEnd('#764ba2');
            }
        } else {
            if (timerGradientStart === '#667eea' && timerGradientEnd === '#764ba2') {
                setTimerGradientStart('#f5576c');
                setTimerGradientEnd('#f093fb');
            }
        }
        // Reset the label to the matching default if it currently equals
        // the previous condition's default. This keeps the user's
        // customisations intact across switches.
        setTimerLabel((current) => current === defaultTimerLabel(conditionType)
            ? current
            : defaultTimerLabel(conditionType));
        // Reset the spots-text to the matching default if it currently
        // equals the previous condition's default. Same rationale as the
        // label reset above — only auto-update what the user hasn't
        // touched.
        setTimerSpotsText((current) => current === defaultTimerSpotsText(conditionType)
            ? current
            : defaultTimerSpotsText(conditionType));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conditionType]);
    const [isCreating, setIsCreating] = useState(false);
    const [createdLink, setCreatedLink] = useState(null);
    const [redirectLinks, setRedirectLinks] = useState([]);
    const [copied, setCopied] = useState('');
    const [expandedLinks, setExpandedLinks] = useState(new Set());
    const [showExplainer, setShowExplainer] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [offers, setOffers] = useState([]);
    const [offersLoading, setOffersLoading] = useState(false);
    const [selectedOffer, setSelectedOffer] = useState(null);
    const [createdOffer, setCreatedOffer] = useState(null); // offer snapshot at creation time
    const [specialOfferPrice, setSpecialOfferPrice] = useState('');
    const [useLovable, setUseLovable] = useState(false);
    const [showSetupModal, setShowSetupModal] = useState(false);

    const toggleExpanded = useCallback((id) => {
        setExpandedLinks(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    }, []);

    useEffect(() => {
        fetchRedirectLinks();
        fetchOffers();
    }, []);

    const fetchOffers = async () => {
        setOffersLoading(true);
        try {
            const token = await getToken();
            const response = await axios.get(
                `https://${SERVER_URL}/api/offers/${username}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            // Archived offers stay on the server (so existing redirect
            // links still reference them) but are hidden from this picker
            // — picking one would just look like a dead offer.
            const list = (response.data.offers || []).filter((o) => !o.is_archived);
            setOffers(list);
            if (list.length > 0) setSelectedOffer(list[0]);
        } catch (error) {
            console.error('Error fetching offers:', error);
        } finally {
            setOffersLoading(false);
        }
    };

    const fetchRedirectLinks = async () => {
        try {
            const token = await getToken();
            const response = await axios.get(
                `https://${SERVER_URL}/api/redirect-links/${username}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );
            setRedirectLinks(response.data.redirectLinks || []);
        } catch (error) {
            console.error('Error fetching urgency links:', error);
        }
    };

    const handleCreateLink = async () => {
        if (!linkName || !offerUrl || !defaultUrl || !conditionValue) {
            alert('Please fill in all fields');
            return;
        }

        setIsCreating(true);
        try {
            const token = await getToken();
            const response = await axios.post(
                `https://${SERVER_URL}/api/create-redirect-link`,
                {
                    username: username,
                    name: linkName,
                    offerUrl: offerUrl,
                    defaultUrl: defaultUrl,
                    conditionType: conditionType,
                    conditionValue: parseInt(conditionValue),
                    showTimer: showTimer,
                    timerPosition: timerPosition,
                    timerGradientStart: timerGradientStart,
                    timerGradientEnd: timerGradientEnd,
                    timerTextColor: timerTextColor,
                    timerLabel: timerLabel,
                    timerSpotsText: timerSpotsText,
                    offerId: selectedOffer?.id || null,
                    specialOfferPrice: specialOfferPrice ? parseFloat(specialOfferPrice) : null,
                    websiteEditor: useLovable ? 'lovable' : null
                },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            setCreatedLink({ ...response.data, websiteEditor: useLovable ? 'lovable' : null });
            setCreatedOffer(selectedOffer ? { ...selectedOffer } : null);
            setShowSetupModal(true);
            setLinkName('');
            setOfferUrl('');
            setDefaultUrl('');
            setConditionValue('');
            setSpecialOfferPrice('');
            setTimerPosition('bottom-right');
            setTimerGradientStart(conditionType === 'timeframe' ? '#667eea' : '#f5576c');
            setTimerGradientEnd(conditionType === 'timeframe' ? '#764ba2' : '#f093fb');
            setTimerTextColor('#ffffff');
            setTimerLabel(defaultTimerLabel(conditionType));
            setTimerSpotsText(defaultTimerSpotsText(conditionType));
            fetchRedirectLinks();
        } catch (error) {
            console.error('Error creating urgency link:', error);
            alert('Error creating urgency link');
        } finally {
            setIsCreating(false);
        }
    };

    const getConditionDescription = () => {
        if (!conditionValue) return '';

        if (conditionType === 'timeframe') {
            return `If < ${conditionValue} hours since first visit, show special offer URL. Otherwise, redirect to default URL.`;
        }

        const metric = conditionType === 'clicks' ? 'clicks' : 'sales';
        return `If ${metric} < ${conditionValue}, show special offer URL. Otherwise, redirect to default URL.`;
    };

    const urgencyLinkScript = (redirectId, conditionType) =>
        `<meta name="revtrack-redirect-id" content="${redirectId}">\n<meta name="revtrack-condition-type" content="${conditionType}">\n<script src="https://d15dfsr886zcp9.cloudfront.net/urgencyLinkScript.js" defer></script>`;

    // Map a position key to the corresponding CSS `top/right/bottom/left` values
    // for the live preview. Mirrors the logic the on-page script will use.
    const positionToCss = (pos) => {
        switch (pos) {
            case 'top-left':     return { top: '20px',    left: '20px',    right: 'auto',  bottom: 'auto' };
            case 'top-right':    return { top: '20px',    right: '20px',   left: 'auto',   bottom: 'auto' };
            case 'bottom-left':  return { bottom: '20px', left: '20px',    right: 'auto',  top: 'auto' };
            case 'bottom-right':
            default:             return { bottom: '20px', right: '20px',   left: 'auto',   top: 'auto' };
        }
    };

    const liveGradient = `linear-gradient(135deg, ${timerGradientStart} 0%, ${timerGradientEnd} 100%)`;

    // The on-page preview is shown inside the form, so we render it as a
    // floating box positioned by the same CSS the badge will use in production.
    const previewBadgeStyle = {
        background: liveGradient,
        color: timerTextColor,
        boxShadow: `0 0 30px ${timerGradientStart}44`
    };

    const urgencySaleScript = (redirectId, price) => {
        const priceTag = price ? `<meta name="RevTrackPrice" content="${price}">\n` : '';
        return `<meta name="revtrack-redirect-id" content="${redirectId}">\n${priceTag}<script src="https://d15dfsr886zcp9.cloudfront.net/urgencyLinkSaleScript.js" defer></script>`;
    };

    const copyToClipboard = (code, label) => {
        navigator.clipboard.writeText(code);
        setCopied(label);
        setTimeout(() => setCopied(''), 2000);
    };

    const conditionLabel = (type) => {
        if (type === 'timeframe') return 'hours';
        return type;
    };

    const handleDeleteLink = async (e, id) => {
        e.stopPropagation();
        if (!window.confirm('Delete this urgency link? This cannot be undone.')) return;

        setDeletingId(id);
        try {
            const token = await getToken();
            await axios.delete(
                `https://${SERVER_URL}/api/redirect-link/${id}`,
                {
                    params: { username },
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );
            setRedirectLinks(prev => prev.filter(l => l.id !== id));
            setExpandedLinks(prev => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
        } catch (error) {
            console.error('Error deleting urgency link:', error);
            alert('Failed to delete urgency link. Please try again.');
        } finally {
            setDeletingId(null);
        }
    };

    if (offersLoading) {
        return (
            <div className="redirect-links-inner">
                <div className="rl-loading">
                    <div className="rl-loading-spinner" />
                    <p className="rl-loading-text">Loading…</p>
                </div>
            </div>
        );
    }

    return (
        <div className="redirect-links-inner">

          {/* ── Page header ── */}
          <div className="rl-page-header">
            <h1 className="rl-page-title">Redirect Links</h1>
            <p className="rl-page-desc">
              Promote a special offer and let RevTrack automatically expire it based on your rules —
              after a set number of sales, clicks, or hours since a visitor's first visit.
              Once the condition is met, visitors are silently redirected to your regular-price page.
            </p>
          </div>

          {/* ── What are urgency links? collapsible explainer ── */}
          <div className="ul-explainer">
            <button
              className="ul-explainer-toggle"
              onClick={() => setShowExplainer(v => !v)}
              aria-expanded={showExplainer}
            >
              <span className={`ul-explainer-chevron${showExplainer ? ' ul-explainer-chevron--open' : ''}`}>▶</span>
              What are urgency links?
            </button>

            {showExplainer && (
              <div className="ul-explainer-body">
                <div className="ul-explainer-scenarios">
                  <div className="ul-scenario">
                    <div className="ul-scenario-icon">🛒</div>
                    <div>
                      <p className="ul-scenario-title">Sales-based expiry</p>
                      <p className="ul-scenario-desc">After <strong>X total sales</strong>, the special offer automatically closes. Anyone who visits afterwards is sent straight to the regular-price page. Perfect for "only 50 spots available" deals. An optional on-screen counter can show how many spots are left.</p>
                    </div>
                  </div>
                  <div className="ul-scenario">
                    <div className="ul-scenario-icon">⏱️</div>
                    <div>
                      <p className="ul-scenario-title">Time-based expiry (per visitor)</p>
                      <p className="ul-scenario-desc">Each visitor gets <strong>X hours</strong> from their first click to claim the offer. Once their personal window expires, they see the default URL. The optional on-screen countdown makes the deadline visible.</p>
                    </div>
                  </div>
                  <div className="ul-scenario">
                    <div className="ul-scenario-icon">🔗</div>
                    <div>
                      <p className="ul-scenario-title">Click-based expiry</p>
                      <p className="ul-scenario-desc">After <strong>X total clicks</strong> on the offer page, the link flips to the default URL. Useful when you want to cap exposure to a deal based on traffic rather than purchases. An optional on-screen counter can show how many spots are left.</p>
                    </div>
                  </div>
                </div>

                <div className="ul-explainer-how">
                  <p className="ul-explainer-how-title">How it works</p>
                  <ol className="ul-explainer-steps">
                    <li>Create a link with your <strong>Special Offer URL</strong>, a <strong>Default URL</strong> (regular price), and the expiry condition.</li>
                    <li>Paste the provided code snippet on your <strong>special offer page</strong> (and your special offer thank-you page if using sales-based expiry).</li>
                    <li>RevTrack tracks the condition in real time. When it's met, all visitors are silently redirected to the default URL — no changes needed on your end.</li>
                  </ol>
                </div>
              </div>
            )}
          </div>

          <div className="rl-two-col">

            {/* ── LEFT: Create form + success banner ── */}
            <div className="rl-left-col">

            {/* Create link card */}
            <div className="redirect-links-card">
                <div className="redirect-links-card-header">
                    <h2 className="card-title">New Urgency Link</h2>
                </div>
                <div className="redirect-links-card-body">
                    <div className="form-section">
                        <label className="form-label">Offer</label>
                        {offersLoading ? (
                            <p className="form-hint">Loading offers…</p>
                        ) : offers.length === 0 ? (
                            <p className="form-hint rl-no-offers">No offers found. Create an offer in the Funnel Setup first.</p>
                        ) : (
                            <select
                                className="form-input"
                                value={selectedOffer?.name || ''}
                                onChange={(e) => {
                                    const found = offers.find(o => o.name === e.target.value);
                                    setSelectedOffer(found || null);
                                }}
                            >
                                {offers.map((o) => (
                                    <option key={o.name} value={o.name}>{o.name}</option>
                                ))}
                            </select>
                        )}
                        <p className="form-hint">The offer this urgency link belongs to — used to generate tracking code</p>
                    </div>

                    <div className="form-section">
                        <label className="form-label">Link Name</label>
                        <input
                            type="text"
                            className="form-input"
                            placeholder="e.g., Black Friday Sale, Launch Promo"
                            value={linkName}
                            onChange={(e) => setLinkName(e.target.value)}
                        />
                        <p className="form-hint">Give this urgency link a memorable name</p>
                    </div>

                    <div className="form-section">
                        <label className="form-label">Special Offer URL</label>
                        <input
                            type="url"
                            className="form-input"
                            placeholder="https://example.com/special-offer"
                            value={offerUrl}
                            onChange={(e) => setOfferUrl(e.target.value)}
                        />
                        <p className="form-hint">The URL where your special offer is hosted</p>
                    </div>

                    <div className="form-section">
                        <label className="form-label">Default URL</label>
                        <input
                            type="url"
                            className="form-input"
                            placeholder="https://example.com/regular-price"
                            value={defaultUrl}
                            onChange={(e) => setDefaultUrl(e.target.value)}
                        />
                        <p className="form-hint">Where to redirect when the condition is met</p>
                    </div>

                    <div className="form-section">
                        <label className="form-label">Routing Rule</label>

                        <div className="rule-inputs">
                            <span className="rule-prefix">If</span>

                            <select
                                className="rule-select"
                                value={conditionType}
                                onChange={(e) => setConditionType(e.target.value)}
                            >
                                <option value="clicks">clicks</option>
                                <option value="sales">sales</option>
                                <option value="timeframe">hours since first visit</option>
                            </select>

                            <span className="rule-operator">&lt;</span>

                            <input
                                type="number"
                                className="rule-input"
                                placeholder={conditionType === 'timeframe' ? 'e.g. 24' : 'e.g. 100'}
                                value={conditionValue}
                                onChange={(e) => setConditionValue(e.target.value)}
                            />
                        </div>

                        <div className="timer-option">
                            <label className="timer-toggle-label" htmlFor="timer-show-toggle">
                                <span className="timer-toggle-text">
                                    {conditionType === 'timeframe'
                                        ? 'Show countdown timer on screen'
                                        : `Show remaining ${conditionType} counter on screen`}
                                </span>
                                <span className="timer-toggle-switch">
                                    <input
                                        id="timer-show-toggle"
                                        type="checkbox"
                                        className="timer-toggle-input"
                                        checked={showTimer}
                                        onChange={(e) => setShowTimer(e.target.checked)}
                                    />
                                    <span className="timer-toggle-track">
                                        <span className="timer-toggle-thumb" />
                                    </span>
                                </span>
                            </label>

                             {showTimer && (
                                <div className="timer-style">
                                    <div className="timer-config-row timer-config-row--label">
                                        <div className="config-input-group config-input-group--label">
                                            <span className="config-label">Label</span>
                                            <input
                                                type="text"
                                                className="form-input timer-label-input"
                                                placeholder={defaultTimerLabel(conditionType)}
                                                value={timerLabel}
                                                maxLength={80}
                                                onChange={(e) => setTimerLabel(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    {/* Custom lower text only applies to clicks/sales
                                        counters (timeframe shows a digit countdown, not
                                        text). `{count}` is replaced with the remaining
                                        value at render time on the live page. */}
                                    {(conditionType === 'clicks' || conditionType === 'sales') && (
                                        <div className="timer-config-row timer-config-row--label">
                                            <div className="config-input-group config-input-group--label">
                                                <span className="config-label">Counter Text</span>
                                                <input
                                                    type="text"
                                                    className="form-input timer-label-input"
                                                    placeholder={defaultTimerSpotsText(conditionType)}
                                                    value={timerSpotsText}
                                                    maxLength={80}
                                                    onChange={(e) => setTimerSpotsText(e.target.value)}
                                                />
                                                <p className="form-hint">Use <code>{'{count}'}</code> to insert the remaining number.</p>
                                            </div>
                                        </div>
                                    )}
                                    <div className="timer-config-row">
                                        <div className="config-input-group">
                                            <span className="config-label">Start</span>
                                            <div className="custom-color-picker" style={{ backgroundColor: timerGradientStart }}>
                                                <input
                                                    type="color"
                                                    value={timerGradientStart}
                                                    onChange={(e) => setTimerGradientStart(e.target.value)}
                                                />
                                            </div>
                                        </div>

                                        <div className="config-input-group">
                                            <span className="config-label">End</span>
                                            <div className="custom-color-picker" style={{ backgroundColor: timerGradientEnd }}>
                                                <input
                                                    type="color"
                                                    value={timerGradientEnd}
                                                    onChange={(e) => setTimerGradientEnd(e.target.value)}
                                                />
                                            </div>
                                        </div>

                                        <div className="config-input-group">
                                            <span className="config-label">Text</span>
                                            <div className="custom-color-picker" style={{ backgroundColor: timerTextColor }}>
                                                <input
                                                    type="color"
                                                    value={timerTextColor}
                                                    onChange={(e) => setTimerTextColor(e.target.value)}
                                                />
                                            </div>
                                        </div>

                                        <div className="config-input-group">
                                            <span className="config-label">Position</span>
                                            <select
                                                className="position-dropdown"
                                                value={timerPosition}
                                                onChange={(e) => setTimerPosition(e.target.value)}
                                            >
                                                <option value="top-left">Top Left</option>
                                                <option value="top-right">Top Right</option>
                                                <option value="bottom-left">Bottom Left</option>
                                                <option value="bottom-right">Bottom Right</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {showTimer && (
                                <div className="timer-preview">
                                    <div className="timer-preview-label">PREVIEW</div>
                                    <div className="timer-preview-stage">
                                        <div className="timer-preview-box" style={previewBadgeStyle}>
                                            <div className="timer-preview-title">
                                                {(timerLabel || defaultTimerLabel(conditionType)).toUpperCase()}
                                            </div>
                                            {conditionType === 'timeframe' ? (
                                                <div className="timer-preview-digits">
                                                    <div className="timer-digit-group">
                                                        <div className="timer-digit">23</div>
                                                        <div className="timer-digit-label">HRS</div>
                                                    </div>
                                                    <div className="timer-separator">:</div>
                                                    <div className="timer-digit-group">
                                                        <div className="timer-digit">45</div>
                                                        <div className="timer-digit-label">MIN</div>
                                                    </div>
                                                    <div className="timer-separator">:</div>
                                                    <div className="timer-digit-group">
                                                        <div className="timer-digit">12</div>
                                                        <div className="timer-digit-label">SEC</div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="timer-spots-count">
                                                    {(() => {
                                                        const total = parseInt(conditionValue, 10);
                                                        const remaining = Number.isFinite(total) ? Math.max(total, 0) : 12;
                                                        const template = (timerSpotsText || '').trim() || defaultTimerSpotsText(conditionType);
                                                        if (template.includes('{count}')) {
                                                            return template.replace(/\{count\}/g, String(remaining));
                                                        }
                                                        return `${remaining} ${template}`.trim();
                                                    })()}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {conditionValue && (
                            <p className="rule-description">
                                {getConditionDescription()}
                            </p>
                        )}
                    </div>

                    <div className="form-section">
                        <label className="form-label">Special Offer Price</label>
                        <input
                            type="number"
                            className="form-input"
                            placeholder="e.g. 97"
                            min="0"
                            step="0.01"
                            value={specialOfferPrice}
                            onChange={(e) => setSpecialOfferPrice(e.target.value)}
                        />
                        <p className="form-hint">The price of the special offer</p>
                    </div>

                    <div className="form-section">
                        <label className="rl-lovable-toggle">
                            <span className="rl-lovable-toggle-label">Do you use Lovable?</span>
                            <img src={lovableLogo} alt="Lovable" className="rl-lovable-toggle-logo" />
                            <div className="rl-lovable-toggle-switch">
                                <input
                                    type="checkbox"
                                    className="rl-lovable-toggle-input"
                                    checked={useLovable}
                                    onChange={(e) => setUseLovable(e.target.checked)}
                                />
                                <span className="rl-lovable-toggle-track">
                                    <span className="rl-lovable-toggle-thumb" />
                                </span>
                            </div>
                        </label>
                        <p className="form-hint">If you build your pages with Lovable, we'll give you a prompt to paste instead of code.</p>
                    </div>

                    <button
                        className="create-link-btn"
                        onClick={handleCreateLink}
                        disabled={isCreating}
                    >
                        {isCreating ? 'Creating…' : 'Create Urgency Link'}
                    </button>
                </div>
            </div>

            </div> {/* /rl-left-col */}

            {/* ── RIGHT: Existing links ── */}
            <div className="rl-right-col">
            <div className="existing-links-section">
                <div className="existing-links-header">
                    <h2 className="card-title">Your Urgency Links</h2>
                </div>

                {redirectLinks.length === 0 ? (
                    <div className="existing-links-empty">
                        <p>No urgency links yet.</p>
                        <p>Create one above to get started.</p>
                    </div>
                ) : (
                    <div className="links-list">
                        {redirectLinks.map((link) => {
                            const isExpanded = expandedLinks.has(link.id);
                            return (
                            <div key={link.id} className={`link-item${isExpanded ? ' link-item--expanded' : ''}`}>

                                {/* Clickable header — toggles collapse */}
                                <div
                                    className="link-item-header link-item-header--toggle"
                                    onClick={() => toggleExpanded(link.id)}
                                    role="button"
                                    aria-expanded={isExpanded}
                                >
                                    <div className="link-item-header-left">
                                        <span className={`link-item-chevron${isExpanded ? ' link-item-chevron--open' : ''}`}>▶</span>
                                        <h3 className="link-item-name">{link.name}</h3>
                                        {link.website_editor === 'lovable' && (
                                            <img src={lovableLogo} alt="Lovable" className="rl-link-lovable-logo" />
                                        )}
                                    </div>
                                    <div className="link-item-header-right">
                                        {(link.condition_type === 'clicks' || link.condition_type === 'sales') && (
                                            <span className="link-stat-badge">
                                                {link.condition_type === 'clicks' ? link.click_count : link.sale_count}
                                                &nbsp;{link.condition_type === 'clicks' ? 'clicks' : 'sales'}
                                            </span>
                                        )}
                                        <span className="link-rule-badge">
                                            if {link.condition_type} &lt; {link.condition_value}
                                        </span>
                                        <button
                                            className="link-delete-btn"
                                            onClick={(e) => handleDeleteLink(e, link.id)}
                                            disabled={deletingId === link.id}
                                            aria-label="Delete urgency link"
                                            title="Delete"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                </div>

                                {/* Collapsible body */}
                                {isExpanded && (
                                    <div className="link-item-body">
                                        {/* Stats */}
                                        <div className="link-stats">
                                            <div className="link-stat">
                                                <span className="link-stat-label">Clicks</span>
                                                <span className="link-stat-value">{(link.click_count || 0).toLocaleString()}</span>
                                            </div>
                                            <div className="link-stat">
                                                <span className="link-stat-label">Sales</span>
                                                <span className="link-stat-value">{(link.sale_count || 0).toLocaleString()}</span>
                                            </div>
                                            <div className="link-stat link-stat--conv">
                                                <span className="link-stat-label">Conv Rate</span>
                                                <span className="link-stat-value">
                                                    {link.click_count > 0
                                                        ? `${((link.sale_count / link.click_count) * 100).toFixed(1)}%`
                                                        : '—'}
                                                </span>
                                            </div>
                                        </div>

                                        {/* URLs */}
                                        <div className="link-meta">
                                            <div className="link-url-row">
                                                <span className="link-url-label">Offer</span>
                                                <span className="link-url-value">{link.offer_url}</span>
                                            </div>
                                            <div className="link-url-row">
                                                <span className="link-url-label">Default</span>
                                                <span className="link-url-value">{link.default_url}</span>
                                            </div>
                                        </div>

                                        {/* Code snippets */}
                                        {(() => {
                                            const isLovableLink = link.website_editor === 'lovable';

                                            const listOfferPageCode = isLovableLink
                                                ? buildLovablePrompt(
                                                    link.offer_name
                                                        ? [urgencyLinkScript(link.id, link.condition_type), buildTrackerScript(link.offer_name, username)]
                                                        : [urgencyLinkScript(link.id, link.condition_type)],
                                                    link.offer_name
                                                        ? [{ raw: urgencyLinkScriptRaw, init: 'initializeUrgencyLinkScript' }, { raw: trackerScriptRaw, init: 'initializeTracker' }]
                                                        : [{ raw: urgencyLinkScriptRaw, init: 'initializeUrgencyLinkScript' }]
                                                  )
                                                : (link.offer_name
                                                    ? `${urgencyLinkScript(link.id, link.condition_type)}\n\n${buildTrackerScript(link.offer_name, username)}`
                                                    : urgencyLinkScript(link.id, link.condition_type));

                                            const listThankYouCode = isLovableLink
                                                ? buildLovablePrompt(
                                                    link.offer_name
                                                        ? [urgencySaleScript(link.id, link.special_offer_price || null), buildThankYouScript(link.offer_name, username, link.special_offer_price || null)]
                                                        : [urgencySaleScript(link.id, link.special_offer_price || null)],
                                                    link.offer_name
                                                        ? [{ raw: urgencyLinkSaleScriptRaw, init: 'initializeUrgencyLinkSaleScript' }, { raw: thankYouScriptRaw, init: 'initializeThankYou' }]
                                                        : [{ raw: urgencyLinkSaleScriptRaw, init: 'initializeUrgencyLinkSaleScript' }]
                                                  )
                                                : (link.offer_name
                                                    ? `${urgencySaleScript(link.id, link.special_offer_price || null)}\n\n${buildThankYouScript(link.offer_name, username, link.special_offer_price || null)}`
                                                    : urgencySaleScript(link.id, link.special_offer_price || null));
                                            return (
                                        <div className="link-snippets">
                                            <div className="code-section">
                                                <div className="code-section-label">
                                                    {link.condition_type === 'sales' && (
                                                        <span className="code-step-badge">1</span>
                                                    )}
                                                    {isLovableLink ? 'Special offer page — Give this prompt to Lovable' : 'Copy the below code as HTML to your page'}
                                                    {link.offer_name && <span className="link-offer-badge">{link.offer_name}</span>}
                                                </div>
                                                <div className="code-block">
                                                    <pre className={`inline-code${isLovableLink ? ' inline-code--scroll' : ''}`}>{listOfferPageCode}</pre>
                                                    <div className="code-block-footer">
                                                        <button
                                                            className="code-copy-btn"
                                                            onClick={(e) => { e.stopPropagation(); copyToClipboard(listOfferPageCode, `link-offer-${link.id}`); }}
                                                        >
                                                            {copied === `link-offer-${link.id}` ? '✓ Copied!' : (isLovableLink ? 'Copy Prompt' : 'Copy Code')}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="code-section">
                                                <div className="code-section-label">
                                                    {link.condition_type === 'sales' && (
                                                        <span className="code-step-badge">2</span>
                                                    )}
                                                    {isLovableLink ? 'Special offer thank-you page — Give this prompt to Lovable' : 'Special offer thank-you page snippet'}
                                                    {link.condition_type !== 'sales' && <span className="code-optional-badge"> — optional</span>}
                                                </div>
                                                <div className="code-block">
                                                    <pre className={`inline-code${isLovableLink ? ' inline-code--scroll' : ''}`}>{listThankYouCode}</pre>
                                                    <div className="code-block-footer">
                                                        <button
                                                            className="code-copy-btn"
                                                            onClick={(e) => { e.stopPropagation(); copyToClipboard(listThankYouCode, `link-sale-${link.id}`); }}
                                                        >
                                                            {copied === `link-sale-${link.id}` ? '✓ Copied!' : (isLovableLink ? 'Copy Prompt' : 'Copy Code')}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                            );
                                        })()}
                                    </div>
                                )}
                            </div>
                            );
                        })}
                    </div>
                )}
            </div>
            </div> {/* /rl-right-col */}

          </div> {/* /rl-two-col */}

          {/* Setup instructions modal */}
          {showSetupModal && createdLink && (() => {
              const isLovableLink = createdLink.websiteEditor === 'lovable';

              const offerPageCode = isLovableLink
                  ? buildLovablePrompt(
                      createdLink.offerName
                          ? [urgencyLinkScript(createdLink.id, createdLink.conditionType), buildTrackerScript(createdLink.offerName, username)]
                          : [urgencyLinkScript(createdLink.id, createdLink.conditionType)],
                      createdLink.offerName
                          ? [{ raw: urgencyLinkScriptRaw, init: 'initializeUrgencyLinkScript' }, { raw: trackerScriptRaw, init: 'initializeTracker' }]
                          : [{ raw: urgencyLinkScriptRaw, init: 'initializeUrgencyLinkScript' }]
                    )
                  : (createdLink.offerName
                      ? `${urgencyLinkScript(createdLink.id, createdLink.conditionType)}\n\n${buildTrackerScript(createdLink.offerName, username)}`
                      : urgencyLinkScript(createdLink.id, createdLink.conditionType));

              const thankYouCode = isLovableLink
                  ? buildLovablePrompt(
                      createdLink.offerName
                          ? [urgencySaleScript(createdLink.id, createdLink.specialOfferPrice || null), buildThankYouScript(createdLink.offerName, username, createdLink.specialOfferPrice || null)]
                          : [urgencySaleScript(createdLink.id, createdLink.specialOfferPrice || null)],
                      createdLink.offerName
                          ? [{ raw: urgencyLinkSaleScriptRaw, init: 'initializeUrgencyLinkSaleScript' }, { raw: thankYouScriptRaw, init: 'initializeThankYou' }]
                          : [{ raw: urgencyLinkSaleScriptRaw, init: 'initializeUrgencyLinkSaleScript' }]
                    )
                  : (createdLink.offerName
                      ? `${urgencySaleScript(createdLink.id, createdLink.specialOfferPrice || null)}\n\n${buildThankYouScript(createdLink.offerName, username, createdLink.specialOfferPrice || null)}`
                      : urgencySaleScript(createdLink.id, createdLink.specialOfferPrice || null));

              return (
              <div className="setup-modal-overlay" onClick={() => setShowSetupModal(false)}>
                  <div className="setup-modal" onClick={(e) => e.stopPropagation()}>

                      {/* Header */}
                      <div className="setup-modal-header">
                          <div className="setup-modal-title-row">
                              <span className="setup-modal-check">✓</span>
                              <div>
                                  <h2 className="setup-modal-title">Urgency Link Created!</h2>
                                  <p className="setup-modal-subtitle">
                                      {isLovableLink
                                          ? 'Give Lovable the prompts below to activate tracking on your pages.'
                                          : 'Add the code snippets below to your pages to activate tracking.'}
                                      {' '}This should be added <b>instead of any other RevTrack code</b> you have on the page.
                                  </p>
                              </div>
                          </div>
                          <button className="setup-modal-close" onClick={() => setShowSetupModal(false)} aria-label="Close">✕</button>
                      </div>

                      {/* Body */}
                      <div className="setup-modal-body">

                          {/* Step 1 */}
                          <div className="setup-step">
                              <div className="setup-step-num">1</div>
                              <div className="setup-step-content">
                                  <p className="setup-step-title">
                                      {isLovableLink
                                          ? <>Give Lovable this prompt for your <strong>special offer landing page</strong></>
                                          : <>Add the following code to your <strong>special offer landing page</strong> as HTML</>}
                                      {createdLink.offerUrl && (
                                          <span className="setup-step-url"> — {createdLink.offerUrl}</span>
                                      )}
                                  </p>
                                  <div className="code-block">
                                      <pre className={`inline-code${isLovableLink ? ' inline-code--scroll' : ''}`}>{offerPageCode}</pre>
                                      <div className="code-block-footer">
                                          <button className="code-copy-btn" onClick={() => copyToClipboard(offerPageCode, 'modal-offer')}>
                                              {copied === 'modal-offer' ? '✓ Copied!' : (isLovableLink ? 'Copy Prompt' : 'Copy Code')}
                                          </button>
                                      </div>
                                  </div>
                              </div>
                          </div>

                          {/* Step 2 */}
                          <div className="setup-step">
                              <div className="setup-step-num">2</div>
                              <div className="setup-step-content">
                                  <p className="setup-step-title">
                                      {isLovableLink
                                          ? <>Give Lovable this prompt for your <strong>special offer thank-you page</strong></>
                                          : <>Add this code to your <strong>special offer thank-you page</strong> (the page shown after a sale) as HTML</>}
                                      {createdLink.conditionType !== 'sales' && (
                                          <span className="code-optional-badge"> — optional</span>
                                      )}
                                  </p>
                                  <div className="code-block">
                                      <pre className={`inline-code${isLovableLink ? ' inline-code--scroll' : ''}`}>{thankYouCode}</pre>
                                      <div className="code-block-footer">
                                          <button className="code-copy-btn" onClick={() => copyToClipboard(thankYouCode, 'modal-sale')}>
                                              {copied === 'modal-sale' ? '✓ Copied!' : (isLovableLink ? 'Copy Prompt' : 'Copy Code')}
                                          </button>
                                      </div>
                                  </div>
                              </div>
                          </div>

                          {/* Common issues */}
                          <details className="setup-issues">
                              <summary className="setup-issues-summary">Common issues</summary>
                              <ul className="setup-issues-list">
                                  <li>
                                      <strong>Script not firing?</strong> Paste the code into the page's HTML — most page builders have a "Custom HTML" or "Header scripts" field. Make sure it appears on every version/variant of that page.
                                  </li>
                                  <li>
                                      <strong>Visitors not being redirected?</strong> Confirm the snippet is on the exact URL you set as your Offer URL, not a different subdomain or path variant.
                                  </li>
                                  <li>
                                      <strong>Sales not being counted?</strong> Make sure the thank-you page snippet (Step 2) is live on the confirmation page your buyers actually land on after purchase.
                                  </li>
                              </ul>
                          </details>

                          <button className="setup-modal-done-btn" onClick={() => setShowSetupModal(false)}>
                              Done — I've added the {isLovableLink ? 'prompts' : 'snippets'}
                          </button>
                      </div>

                  </div>
              </div>
              );
          })()}

        </div>
    );
};

export default RedirectLinks;
